import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {FormattedMessage} from "react-intl";
import {UserApi} from "../../api/UserApi";
import {IUserInfo, UserInfo, UserTypeEnum} from "../../model/UserInfo.model";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {ErrorComponent} from "../base/Error.component";
import {getTranslation} from "../../utils/translationUtil";
import {UserSearchDropdown} from "./UserSearchDropdown.component";

export interface IPlanUsersTabProps {
    planId: number;
    planName: string;
}

type ToastType = "success" | "error";

interface IToastState {
    visible: boolean;
    message: string;
    type: ToastType;
}

export const PlanUsersTab: React.FunctionComponent<IPlanUsersTabProps> = (props) => {
    const [allUsers, setAllUsers] = useState<IUserInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [toast, setToast] = useState<IToastState>({visible: false, message: "", type: "success"});

    useEffect(() => {
        setLoading(true);
        UserApi.requestUsers().then((response) => {
            response.data ? setAllUsers(response.data): setError(true);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }, []);

    const assignedUsers = useMemo(() => {
        return allUsers
            .filter((u) => u.planIds.includes(props.planId))
            .sort((a, b) => a.email.localeCompare(b.email));
    }, [allUsers, props.planId]);

    const formatRole = (userType: UserTypeEnum): string => {
        return userType.charAt(0) + userType.slice(1).toLowerCase();
    };

    const showToast = useCallback((message: string, type: ToastType = "success") => {
        setToast({visible: true, message, type});
        setTimeout(() => {
            setToast({visible: false, message: "", type: "success"});
        }, 3000);
    }, []);

    const handleAssignUser = useCallback((user: IUserInfo) => {
        const updatedPlanIds = [...user.planIds, props.planId];
        UserApi.updateUser(user.arbtSecurityId, user.userType, updatedPlanIds).then(() => {
                setAllUsers((prev) => prev.map((u) =>
                    u.arbtSecurityId === user.arbtSecurityId
                    ? new UserInfo(u.firstName, u.lastName, u.email, u.lastLoginDate,
                        u.userType, u.arbtSecurityId, updatedPlanIds)
                    : u,
                ));
                showToast(`${user.firstName} ${user.lastName} ${getTranslation("admin.plan-users.added", "added to plan")}`);
            }
        ).catch(() => {
            showToast(
                `${getTranslation("admin.plan-users.error-assign", "Failed to assign")} ${user.firstName} ${user.lastName}`,
                "error",
            );
        });
    }, [props.planId, showToast]);

    const handleRemoveUser = useCallback((user: IUserInfo) => {
        UserApi.removeUserFromPlan(user.arbtSecurityId, props.planId).then(() => {
            setAllUsers((prev) => prev.map((u) =>
                u.arbtSecurityId === user.arbtSecurityId
                    ? new UserInfo(u.firstName, u.lastName, u.email, u.lastLoginDate,
                        u.userType, u.arbtSecurityId, u.planIds.filter((id) => id !== props.planId))
                    : u,
            ));
            showToast(`${user.firstName} ${user.lastName} ${getTranslation("admin.plan-users.removed", "removed from plan")}`);
        }).catch(() => {
            showToast(
                `${getTranslation("admin.plan-users.error-remove", "Failed to remove")} ${user.firstName} ${user.lastName}`,
                "error",
            );
        });
    }, [props.planId, showToast]);

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorComponent />;

    const renderSearchDropdown = () => {
      return <div className="plan-users-tab__header">
          <UserSearchDropdown
              allUsers={allUsers}
              planId={props.planId}
              planName={props.planName}
              onAssignUser={handleAssignUser}
          />
          <div className="plan-users-tab__count">
            <span className="plan-users-tab__count-badge" data-testid="plan-users-tab__count">
                {assignedUsers.length}{" "}
                {assignedUsers.length === 1
                    ? getTranslation("admin.plan-users.user-assigned", "user assigned")
                    : getTranslation("admin.plan-users.users-assigned", "users assigned")}
            </span>
          </div>
      </div>;
    };

    const renderEmptyUserMessage = () => {
        return <div className="plan-users-tab__empty" data-testid="plan-users-tab__empty">
            <div className="plan-users-tab__empty-icon">
                <i className="fa-solid fa-users" />
            </div>
            <h3><FormattedMessage id="admin.plan-users.no-users" defaultMessage="No users assigned" /></h3>
            <p><FormattedMessage id="admin.plan-users.no-users-hint" defaultMessage="Search for users above to add them to this plan." /></p>
        </div>;
    };

    const renderUserTable = () => {
        return <div className="plan-users-tab__table-wrapper">
            <table className="plan-users-tab__table"
                   aria-label={getTranslation("admin.plan-users.table-aria", "Users assigned to this plan")}>
                <thead>
                <tr>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.first-name" defaultMessage="First Name" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.last-name" defaultMessage="Last Name" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.email" defaultMessage="Email" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.role" defaultMessage="Role" /></th>
                    <th scope="col"><FormattedMessage id="admin.plan-users.th.last-login" defaultMessage="Last Login" /></th>
                    <th scope="col"><FormattedMessage id="asset-class.Equity" defaultMessage="Actions" /></th>
                </tr>
                </thead>
                <tbody>
                {assignedUsers.map((user) => (
                    <tr key={user.arbtSecurityId} data-testid={`user-row-${user.arbtSecurityId}`}>
                        <td>{user.firstName}</td>
                        <td>{user.lastName}</td>
                        <td className="plan-users-tab__email">{user.email}</td>
                        <td className="plan-users-tab__role">{formatRole(user.userType)}</td>
                        <td className="plan-users-tab__last-login">
                            {user.lastLoginDate || getTranslation("admin.plan-users.never", "Never")}
                        </td>
                        <td className="plan-users-tab__actions">
                            <div className="fas fa-trash trash-can clickable"
                                 onClick={() => handleRemoveUser(user)}
                                 role="button" tabIndex={0}
                                 aria-label={`${getTranslation("admin.plan-users.remove-aria", "Remove")} ${user.firstName} ${user.lastName} ${getTranslation("admin.plan-users.from-plan", "from plan")}`}
                                 onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRemoveUser(user); } }}
                                 data-testid={`plan-users__remove-${user.arbtSecurityId}`} />
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>;
    };

    const renderToast = () => {
        return <div className={`plan-users-tab__toast${toast.type === "error" ? " plan-users-tab__toast--error" : ""}`}
                    role="status" aria-live="polite" data-testid="plan-users-tab__toast">
                <span className={toast.type === "error" ? "plan-users__toast-icon--error" : "plan-users__toast-icon--success"}
                      aria-hidden="true">
                    {toast.type === "error" ? "\u2717" : "\u2713"}
                </span>
            {toast.message}
        </div>;
    };

    return (
        <div className="plan-users-tab__container" data-testid="plan-users-tab__container">
            {renderSearchDropdown()}
            {assignedUsers.length === 0 ? renderEmptyUserMessage() : renderUserTable()}
            {toast.visible && renderToast()}
        </div>
    );
};
